import React, { useCallback, useEffect, useRef } from 'react';
import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';
import './CardStackMolecule.scss';

export type ViewMode = 'stack' | 'fan';
export type NavigationLayout = 'horizontal' | 'vertical';

export interface CardStackMoleculeProps {
  /** Array of card elements to render */
  children: React.ReactNode;
  /** Currently visible card index */
  currentIndex: number;
  /** Total number of cards */
  totalCount: number;
  /** Display mode: stack (single visible) or fan (multiple visible) */
  viewMode: ViewMode;
  /** Navigate to next card */
  onNavigateNext: () => void;
  /** Navigate to previous card */
  onNavigatePrev: () => void;
  /** Toggle between stack and fan view */
  onToggleViewMode?: () => void;
  /** Empty state message when no cards */
  emptyMessage?: string;
  /** Empty state icon */
  emptyIcon?: string;
  /** Compact mode for smaller displays (hides view toggle, smaller controls) */
  compact?: boolean;
  /** Navigation layout: horizontal (top) or vertical (pill on right side) */
  navigationLayout?: NavigationLayout;
  /** Additional CSS classes */
  className?: string;
}

/**
 * CardStackMolecule - Container for displaying cards in stack or fan layout
 * Provides navigation controls and view mode toggle
 */
const CardStackMolecule: React.FC<CardStackMoleculeProps> = ({
  children,
  currentIndex,
  totalCount,
  viewMode,
  onNavigateNext,
  onNavigatePrev,
  onToggleViewMode,
  emptyMessage,
  emptyIcon = 'pi-check-circle',
  compact = false,
  navigationLayout = 'horizontal',
  className = '',
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLElement>(null);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        onNavigatePrev();
      } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        onNavigateNext();
      }
    },
    [onNavigateNext, onNavigatePrev]
  );

  // Add keyboard event listeners
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('keydown', handleKeyDown);
    return () => {
      container.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  // Convert children to array for indexing
  const childArray = React.Children.toArray(children);

  // Determine which cards to show based on view mode
  const getCardStyle = (index: number): React.CSSProperties => {
    if (viewMode === 'stack') {
      // Stack mode: only show current card
      if (index === currentIndex) {
        return {
          opacity: 1,
          transform: 'translateX(0) scale(1)',
          zIndex: totalCount - index,
          pointerEvents: 'auto',
        };
      }
      // Hide other cards
      return {
        opacity: 0,
        transform: index < currentIndex ? 'translateX(-100%)' : 'translateX(100%)',
        zIndex: 0,
        pointerEvents: 'none',
      };
    }

    // Fan mode: show current + next 2 cards stacked
    const offset = index - currentIndex;
    if (offset < 0 || offset > 2) {
      return {
        opacity: 0,
        transform: 'translateY(100px) scale(0.8)',
        zIndex: 0,
        pointerEvents: 'none',
      };
    }

    return {
      opacity: 1 - offset * 0.25,
      transform: `translateY(${offset * 16}px) scale(${1 - offset * 0.04})`,
      zIndex: totalCount - offset,
      pointerEvents: offset === 0 ? 'auto' : 'none',
    };
  };

  // Empty state
  if (totalCount === 0) {
    return (
      <div className={`card-stack-molecule card-stack-molecule--empty ${className}`}>
        <div className="card-stack-molecule__empty-state">
          <i className={`pi ${emptyIcon} card-stack-molecule__empty-icon`} />
          <p className="card-stack-molecule__empty-message">
            {emptyMessage || t('armillaryActions.empty.notifications')}
          </p>
        </div>
      </div>
    );
  }

  const canNavigatePrev = currentIndex > 0;
  const canNavigateNext = currentIndex < totalCount - 1;

  return (
    <section
      ref={containerRef}
      className={`card-stack-molecule card-stack-molecule--${viewMode} card-stack-molecule--${navigationLayout} ${compact ? 'card-stack-molecule--compact' : ''} ${className}`}
      aria-label={t('armillaryActions.cardStack.ariaLabel')}
    >
      {/* Horizontal layout: Controls header at top */}
      {navigationLayout === 'horizontal' && (
        <div className="card-stack-molecule__controls">
          <div className="card-stack-molecule__nav">
            <Button
              icon="pi pi-chevron-left"
              className="card-stack-molecule__nav-btn"
              onClick={onNavigatePrev}
              disabled={!canNavigatePrev}
              aria-label={t('armillaryActions.actions.previous')}
              text
              rounded
            />
            <span className="card-stack-molecule__position">
              {t('armillaryActions.position', { current: currentIndex + 1, total: totalCount })}
            </span>
            <Button
              icon="pi pi-chevron-right"
              className="card-stack-molecule__nav-btn"
              onClick={onNavigateNext}
              disabled={!canNavigateNext}
              aria-label={t('armillaryActions.actions.next')}
              text
              rounded
            />
          </div>
          {!compact && onToggleViewMode && (
            <Button
              icon={viewMode === 'stack' ? 'pi pi-th-large' : 'pi pi-list'}
              className="card-stack-molecule__view-toggle"
              onClick={onToggleViewMode}
              aria-label={t('armillaryActions.actions.toggleView')}
              text
              rounded
            />
          )}
        </div>
      )}

      {/* Cards container with vertical navigation pill (if vertical layout) */}
      <div className="card-stack-molecule__cards-wrapper">
        <ul className="card-stack-molecule__cards" aria-live="polite">
          {childArray.map((child, index) => (
            <li
              key={(child as React.ReactElement)?.key ?? `card-${index}`}
              className={`card-stack-molecule__card ${index === currentIndex ? 'card-stack-molecule__card--active' : ''}`}
              style={getCardStyle(index)}
              aria-hidden={index !== currentIndex}
            >
              {child}
            </li>
          ))}
        </ul>

        {/* Vertical layout: Navigation pill on right side */}
        {navigationLayout === 'vertical' && totalCount > 1 && (
          <div className="card-stack-molecule__vertical-nav">
            <button
              className="card-stack-molecule__vertical-nav-btn"
              onClick={onNavigatePrev}
              disabled={!canNavigatePrev}
              aria-label={t('armillaryActions.actions.previous')}
              type="button"
            >
              <i className="pi pi-chevron-up" />
            </button>
            <div className="card-stack-molecule__vertical-nav-divider" />
            <button
              className="card-stack-molecule__vertical-nav-btn"
              onClick={onNavigateNext}
              disabled={!canNavigateNext}
              aria-label={t('armillaryActions.actions.next')}
              type="button"
            >
              <i className="pi pi-chevron-down" />
            </button>
          </div>
        )}
      </div>

      {/* Counter and dots below cards (vertical layout) */}
      {navigationLayout === 'vertical' && totalCount > 1 && (
        <div className="card-stack-molecule__footer">
          <button
            className="card-stack-molecule__counter card-stack-molecule__counter--clickable"
            onClick={onToggleViewMode}
            type="button"
            aria-label={viewMode === 'stack'
              ? t('armillaryActions.actions.showAll')
              : t('armillaryActions.actions.collapse')}
          >
            {viewMode === 'stack'
              ? t('armillaryActions.showAll', { total: totalCount })
              : t('armillaryActions.position', { current: currentIndex + 1, total: totalCount })}
          </button>
          {totalCount <= 10 && (
            <div className="card-stack-molecule__dots" role="tablist" aria-label={t('armillaryActions.cardStack.dotsAriaLabel')}>
              {Array.from({ length: totalCount }).map((_, index) => (
                <button
                  key={`dot-v-${index}`}
                  className={`card-stack-molecule__dot ${index === currentIndex ? 'card-stack-molecule__dot--active' : ''}`}
                  aria-selected={index === currentIndex}
                  aria-label={`${t('armillaryActions.cardStack.goToCard')} ${index + 1}`}
                  role="tab"
                  tabIndex={index === currentIndex ? 0 : -1}
                  onClick={() => {
                    // Navigate to clicked dot
                    if (index < currentIndex) {
                      for (let i = currentIndex; i > index; i--) {
                        onNavigatePrev();
                      }
                    } else if (index > currentIndex) {
                      for (let i = currentIndex; i < index; i++) {
                        onNavigateNext();
                      }
                    }
                  }}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Dot indicators (horizontal layout only) */}
      {navigationLayout === 'horizontal' && totalCount > 1 && totalCount <= 10 && (
        <div className="card-stack-molecule__dots" role="tablist" aria-label={t('armillaryActions.cardStack.dotsAriaLabel')}>
          {Array.from({ length: totalCount }).map((_, index) => (
            <button
              key={`dot-h-${index}`}
              className={`card-stack-molecule__dot ${index === currentIndex ? 'card-stack-molecule__dot--active' : ''}`}
              aria-selected={index === currentIndex}
              aria-label={`${t('armillaryActions.cardStack.goToCard')} ${index + 1}`}
              role="tab"
              tabIndex={index === currentIndex ? 0 : -1}
              onClick={() => {
                // Navigate to clicked dot
                if (index < currentIndex) {
                  for (let i = currentIndex; i > index; i--) {
                    onNavigatePrev();
                  }
                } else if (index > currentIndex) {
                  for (let i = currentIndex; i < index; i++) {
                    onNavigateNext();
                  }
                }
              }}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export default CardStackMolecule;
